import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type TFunction } from "i18next";
import {
  ArrowRight,
  CircleCheck,
  CircleX,
  Clock,
  type LucideIcon,
  MessageSquare,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { OFFER_TYPES } from "../../../../constants";
import {
  formatNumber,
  getCurrencySymbolFromCache,
  getLastOfferUpdateDatetime,
} from "../../../../utils";
import { formatAdminAreaChain } from "../../../../utils/adminArea";
import { getLocalisedName } from "../../../../utils/i18n";
import { formatDateCompact } from "../../../../utils/relative-time";
import { Carousel } from "../../../Deposits/DepositCard/Carousel";
import {
  deliveryCostAmount,
  demandCostState,
  isDemandLine,
  latestDemandDeliveryCost,
} from "../../../../utils/delivery-cost";
import {
  activeProposalTerms,
  canRecallOffer,
  commentsButtonLabelKey,
  demandDeliveryCostMove,
  formatOfferDateTime,
  formatQuantityRange,
  formatTotalPrice,
  formatUnitPrice,
  getActiveProposal,
  isBuyerDeliveryTurn,
  lastKnownPrice,
  offerBaselineTerms,
  offerBriefKey,
  offerDisplayStatus,
  offerNeedsResponse,
  offerPictureUrls,
  offerTermChanges,
  offerTerms,
  resolveDeliveryLocation,
  TermMove,
  termsTotal,
} from "../../utils";
import { type DeliveryLookups } from "../../types";
import { DeliveryActionButtons, type DeliveryDialogKind } from "../DeliveryActionButtons";
import { DeliveryConfirmation, DemandTerms, Offer, OfferStatus } from "./interfaces";

export interface OfferCardProps {
  offer: Offer;
  onViewComments: (offer: Offer) => void;
  onRecall: (offer: Offer) => void;
  onAggregatorDetails: (aggregatorId: number) => void;
  // Opens the demand's delivery-cost negotiation; absent where the cost cannot be negotiated (read-only).
  onReviewDeliveryCost?: (demandId: number) => void;
  onDeliveryAction?: (kind: DeliveryDialogKind, offer: Offer) => void;
  deliveryLookups: DeliveryLookups;
  hideQuality?: boolean;
  hideRecall?: boolean;
  showBuyer?: boolean;
  readOnly?: boolean;
}

export const offerCurrencySymbol = (t: TFunction): string =>
  getCurrencySymbolFromCache() || t("offerCard.currencyUnavailable");

/** "Maize · Grade 1", the grade dropped where a demand carries none (school procurement). */
export const offerCommodityTitle = (offer: Offer, hideQuality = false): string => {
  const commodity = getLocalisedName(offer.buy_commodity);
  const grade = hideQuality ? "" : getLocalisedName(offer.quality_grade) || offer.quality;
  return grade ? `${commodity} · ${grade}` : commodity;
};

const termsSource = (offer: Offer) =>
  activeProposalTerms(offer) ?? { ...offer, price: lastKnownPrice(offer) };

const offerValueCaption = (
  t: TFunction,
  source: ReturnType<typeof termsSource>,
  currency: string,
  unit: string
): string => {
  const headline = t("representativeOffers.card.headlineTerms", {
    quantity: `${formatQuantityRange(source.minimum_quantity, source.maximum_quantity)} ${unit}`,
    price: formatUnitPrice(source.price, currency, unit),
  });
  const deliveryCost = deliveryCostAmount(source.delivery_cost);
  return deliveryCost > 0
    ? `${headline} ${t("representativeOffers.card.plusDeliveryCost", {
        amount: `${currency}${formatNumber(deliveryCost)}`,
      })}`
    : headline;
};

/** `overrides` let a demand group tell the same sentence about its summed lines. */
export const offerBriefText = (
  offer: Offer,
  t: TFunction,
  currency: string,
  unit: string,
  deliveryLookups: DeliveryLookups,
  overrides?: Record<string, string>
): string => {
  const [location, ...address] = resolveDeliveryLocation(
    offer,
    getActiveProposal(offer),
    deliveryLookups,
    {
      nameUnavailable: t("offerCard.nameUnavailable"),
      addressUnavailable: t("offerCard.addressUnavailable"),
      unknownDeliveryType: t("offerCard.unknownDeliveryType"),
    }
  );

  return t(offerBriefKey(offer), {
    aggregator: offer.tagged_aggregator?.name,
    location,
    address: address.join(", "),
    date: formatDateCompact(getLastOfferUpdateDatetime(offer, getActiveProposal(offer))),
    exchangeDate: formatOfferDateTime(offer.planned_exchange_time),
    agreed: `${formatNumber(offerBaselineTerms(offer).minimumQuantity)} ${unit}`,
    collected: `${formatNumber(offerTerms(offer).minimumQuantity)} ${unit}`,
    total: `${currency}${formatNumber(termsTotal(offerTerms(offer)))}`,
    ...overrides,
  });
};

export type OfferBandTone = "warning" | "success" | "destructive";

export const offerBandTone = (offer: Offer): OfferBandTone => {
  if (
    offer.delivery_confirmation === DeliveryConfirmation.DisputedByAggregator ||
    offer.delivery_confirmation === DeliveryConfirmation.DisputedByBuyer
  ) {
    return "destructive";
  }
  switch (offer.status) {
    case OfferStatus.ExchangeDone:
      return offer.delivery_confirmation === DeliveryConfirmation.AwaitingBuyer
        ? "warning"
        : "success";
    case OfferStatus.Recalled:
    case OfferStatus.Rejected:
    case OfferStatus.ExchangeCanceled:
      return "destructive";
    default:
      return "warning";
  }
};

const CHIP_TONES: Record<OfferBandTone, string> = {
  warning: "border-warning-300 text-warning-800",
  success: "border-success-300 text-success-800",
  destructive: "border-danger-300 text-danger-800",
};

const BAND_TONES: Record<OfferBandTone, string> = {
  warning: "border-warning-200 bg-warning-50",
  success: "border-success-200 bg-success-50",
  destructive: "border-danger-200 bg-danger-50",
};

const BAND_ICONS: Record<OfferBandTone, { Icon: LucideIcon; className: string }> = {
  warning: { Icon: Clock, className: "text-warning-500" },
  success: { Icon: CircleCheck, className: "text-success-500" },
  destructive: { Icon: CircleX, className: "text-danger-500" },
};

export const ChangeChip = ({
  label,
  move,
  format,
  tone = "warning",
}: {
  label: string;
  move: TermMove;
  format: (value: number) => string;
  tone?: OfferBandTone;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs",
      CHIP_TONES[tone]
    )}
  >
    {label}
    <span className="font-semibold">{format(move.from)}</span>
    <ArrowRight className="size-3" />
    <span className="font-semibold">{format(move.to)}</span>
  </span>
);

/** Opens the offer's thread; its label and emphasis follow the offer's status. */
export const OfferActionButton = ({
  offer,
  onViewComments,
  size,
}: {
  offer: Offer;
  onViewComments: (offer: Offer) => void;
  size?: "sm";
}) => {
  const { t } = useTranslation();

  return (
    <WFPButton
      size={size}
      variant={offerNeedsResponse(offer) && !isBuyerDeliveryTurn(offer) ? "default" : "secondary"}
      onClick={() => onViewComments(offer)}
    >
      {t(commentsButtonLabelKey(offer))}
    </WFPButton>
  );
};

/** Where the demand's delivery cost stands; nothing while an open cost has no proposal pending. */
export const DemandCostBadge = ({ terms, offers }: { terms: DemandTerms; offers: Offer[] }) => {
  const { t } = useTranslation();
  const currency = offerCurrencySymbol(t);
  const prefix = "representativeOffers.demand.deliveryCost";

  switch (demandCostState(terms)) {
    case "awaitingYou":
      return (
        <Badge variant="warning">
          {t(`${prefix}.aggregatorProposes`, {
            amount: `${currency}${formatNumber(
              deliveryCostAmount(terms.pending_delivery_cost_proposal?.delivery_cost)
            )}`,
          })}
        </Badge>
      );
    case "awaitingAggregator":
      return <Badge>{t(`${prefix}.awaitingAggregator`)}</Badge>;
    case "agreed":
      return <Badge variant="success">{t(`${prefix}.agreed`)}</Badge>;
    case "paid": {
      const line = offers.find(offer => offer.id === terms.delivery_cost_paid_offer);
      return (
        <Badge variant="success">
          {line
            ? t(`${prefix}.paidWith`, { commodity: getLocalisedName(line.buy_commodity) })
            : t(`${prefix}.paid`)}
        </Badge>
      );
    }
    default:
      return null;
  }
};

/**
 * Opens the demand's delivery-cost negotiation, only while the aggregator's proposal waits for the
 * buyer: the buyer answers (accept or counter) and never opens the negotiation itself.
 */
export const ReviewDeliveryCostButton = ({
  terms,
  onReviewDeliveryCost,
  size,
}: {
  terms: DemandTerms | null | undefined;
  onReviewDeliveryCost?: (demandId: number) => void;
  size?: "sm";
}) => {
  const { t } = useTranslation();

  if (!terms || demandCostState(terms) !== "awaitingYou" || !onReviewDeliveryCost) {
    return null;
  }

  return (
    <WFPButton size={size} onClick={() => onReviewDeliveryCost(terms.id)}>
      {t("representativeOffers.demand.deliveryCost.review")}
    </WFPButton>
  );
};

/** Reference, status, commodity and value: everything the row says without expanding. */
export const OfferCardHeader = ({
  reference,
  status,
  updatedAt,
  title,
  subtitle,
  value,
  valueCaption,
  leading,
  trailing,
}: {
  leading?: ReactNode;
  reference: string;
  status: ReactNode;
  updatedAt: string;
  title: string;
  subtitle: ReactNode;
  value: string;
  valueCaption: string;
  trailing?: ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
      {leading}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
            {reference}
          </span>
          <span className="text-muted-foreground text-xs">
            {t("representativeOffers.card.updated", { date: updatedAt })}
          </span>
          {status}
        </div>
        <p className="font-semibold text-foreground text-lg">{title}</p>
        {subtitle}
      </div>

      <div className="flex shrink-0 items-start gap-3 sm:justify-end">
        <div className="flex flex-col sm:items-end">
          <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
            {t("representativeOffers.card.offerValue")}
          </span>
          <span className="font-semibold text-2xl text-foreground">{value}</span>
          <span className="text-muted-foreground text-sm">{valueCaption}</span>
        </div>
        {trailing}
      </div>
    </div>
  );
};

export const OfferActionBand = ({
  message,
  chips,
  actions,
  tone = "warning",
}: {
  message: string;
  chips?: ReactNode;
  actions: ReactNode;
  tone?: OfferBandTone;
}) => {
  const { Icon, className } = BAND_ICONS[tone];

  return (
    <div
      className={cn(
        "flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
        BAND_TONES[tone]
      )}
    >
      <div className="flex gap-2">
        <Icon className={cn("mt-0.5 size-4 shrink-0", className)} />
        <div className="flex flex-col gap-2">
          <p className="text-foreground text-sm">{message}</p>
          {chips && <div className="flex flex-wrap gap-2">{chips}</div>}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-2">{actions}</div>
    </div>
  );
};

export const AggregatorLine = ({
  offer,
  onAggregatorDetails,
}: Pick<OfferCardProps, "offer" | "onAggregatorDetails">) => {
  const name = offer.tagged_aggregator?.name;
  const address = formatAdminAreaChain(offer.tagged_aggregator_admin_area);

  if (!name) {
    return null;
  }

  return (
    <p className="flex min-w-0 text-muted-foreground text-sm">
      {offer.tagged_aggregator?.id ? (
        <WFPButton
          type="button"
          variant="link"
          className="h-auto shrink-0 p-0 font-normal text-muted-foreground text-sm"
          onClick={() => onAggregatorDetails(offer.tagged_aggregator.id)}
        >
          {name}
        </WFPButton>
      ) : (
        <span className="shrink-0">{name}</span>
      )}
      {address && (
        <span className="truncate" title={address}>
          &nbsp;· {address}
        </span>
      )}
    </p>
  );
};

export const BuyerLine = ({ offer }: { offer: Offer }) => {
  const { t } = useTranslation();
  const buyer = offer.trader?.name;
  const person = offer.trader_representative?.person;
  const representative = [person?.first_name, person?.last_name].filter(Boolean).join(" ");

  if (!buyer && !representative) {
    return null;
  }

  return (
    <p className="text-muted-foreground text-sm">
      {t("representativeOffers.card.buyer", {
        buyer: [buyer, representative].filter(Boolean).join(" · "),
      })}
    </p>
  );
};

/** A single-commodity offer: header plus status band, nothing to expand. */
export const OfferCard = ({
  offer,
  onViewComments,
  onRecall,
  onAggregatorDetails,
  onReviewDeliveryCost,
  onDeliveryAction,
  deliveryLookups,
  hideQuality = false,
  hideRecall = false,
  showBuyer = false,
  readOnly = false,
}: OfferCardProps) => {
  const { t } = useTranslation();

  if (!offer?.id) {
    return null;
  }

  const displayStatus = offerDisplayStatus(offer, readOnly);

  const unit = offer.unit_of_measure?.code;
  const currency = offerCurrencySymbol(t);
  // A demand with a single line reads as a plain offer: the demand's cost stands in for the line's.
  const demandTerms = isDemandLine(offer) ? offer.demand_terms : undefined;
  const source = demandTerms
    ? { ...termsSource(offer), delivery_cost: latestDemandDeliveryCost(demandTerms) }
    : termsSource(offer);
  const changes = demandTerms
    ? { ...offerTermChanges(offer), deliveryCost: demandDeliveryCostMove(demandTerms) }
    : offerTermChanges(offer);
  const costBadge = demandTerms && demandCostState(demandTerms) && (
    <DemandCostBadge terms={demandTerms} offers={[offer]} />
  );
  const collected = offer.status === OfferStatus.ExchangeDetailsUpdated;
  const tone = offerBandTone(offer);

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <OfferCardHeader
        leading={<Carousel className="size-24 shrink-0" imageUrls={offerPictureUrls(offer)} />}
        reference={`${t(
          offer.offer_type === OFFER_TYPES.MARKET_OFFER
            ? "marketOfferView.marketOffer"
            : "marketOfferView.directOffer"
        )} #${offer.id}`}
        status={
          <>
            <Badge variant={displayStatus.variant}>
              {t(displayStatus.labelKey, displayStatus.params)}
            </Badge>
            {!!offer.comment_count && (
              <Badge variant="outline" aria-label={t("commentsModal.comments")}>
                <MessageSquare className="fill-current" />
                {offer.comment_count}
              </Badge>
            )}
          </>
        }
        updatedAt={formatOfferDateTime(getLastOfferUpdateDatetime(offer, getActiveProposal(offer)))}
        title={offerCommodityTitle(offer, hideQuality)}
        subtitle={
          <>
            <AggregatorLine offer={offer} onAggregatorDetails={onAggregatorDetails} />
            {showBuyer && <BuyerLine offer={offer} />}
          </>
        }
        value={formatTotalPrice(source, currency)}
        valueCaption={offerValueCaption(t, source, currency, unit)}
      />

      <OfferActionBand
        tone={tone}
        message={offerBriefText(
          offer,
          t,
          currency,
          unit,
          deliveryLookups,
          demandTerms
            ? {
                total: `${currency}${formatNumber(
                  termsTotal(offerTerms(offer)) +
                    deliveryCostAmount(latestDemandDeliveryCost(demandTerms))
                )}`,
              }
            : undefined
        )}
        chips={
          (changes.quantity || changes.price || changes.deliveryCost || costBadge) && (
            <>
              {costBadge}
              {changes.quantity && (
                <ChangeChip
                  label={t(
                    collected
                      ? "representativeOffers.card.change.collected"
                      : "representativeOffers.card.change.quantity"
                  )}
                  tone={tone}
                  move={changes.quantity}
                  format={value => `${formatNumber(value)} ${unit}`}
                />
              )}
              {changes.price && (
                <ChangeChip
                  label={t("representativeOffers.card.change.price")}
                  tone={tone}
                  move={changes.price}
                  format={value => formatUnitPrice(value, currency, unit)}
                />
              )}
              {changes.deliveryCost && (
                <ChangeChip
                  label={t("representativeOffers.card.change.deliveryCost")}
                  tone={tone}
                  move={changes.deliveryCost}
                  format={value => `${currency}${formatNumber(value)}`}
                />
              )}
            </>
          )
        }
        actions={
          <>
            {!hideRecall && canRecallOffer(offer.status) && (
              <WFPButton variant="destructive-secondary" onClick={() => onRecall(offer)}>
                {t("offerCard.recallOffer")}
              </WFPButton>
            )}
            <ReviewDeliveryCostButton
              terms={demandTerms}
              onReviewDeliveryCost={onReviewDeliveryCost}
            />
            <DeliveryActionButtons
              offer={offer}
              readOnly={readOnly}
              onDeliveryAction={onDeliveryAction}
            />
            <OfferActionButton offer={offer} onViewComments={onViewComments} />
          </>
        }
      />
    </Card>
  );
};
